import { useEffect, useState } from "react";
import axios from "axios";
import QRCode from "react-qr-code";
import { Download, RefreshCw, ShieldCheck, Smartphone } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Public, no-auth landing page: crm.wisedrive.in/m
// Always resolves to the newest mechanic APK (backend queries EAS, cached ~10 min).
export default function MechanicAppDownload() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const pageUrl =
    typeof window !== "undefined" ? `${window.location.origin}/m` : "";
  const downloadUrl = `${API}/api/mechanic-app/download`;

  const load = () => {
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/api/mechanic-app/latest`)
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch(() => {
        setError(
          "Couldn't fetch the latest build right now. Please try again in a moment."
        );
        setLoading(false);
      });
  };

  useEffect(load, []);

  const fmtDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return null;
    }
  };

  return (
    <div
      data-testid="mechanic-download-page"
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(1200px 600px at 50% -10%, #3a3fb0 0%, #2E3192 35%, #1b1d55 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: "#ffffff",
          borderRadius: 24,
          boxShadow: "0 30px 80px rgba(0,0,0,0.35)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: "#2E3192",
            padding: "28px 28px 22px",
            color: "#fff",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 64,
              height: 64,
              borderRadius: 18,
              background: "rgba(255,255,255,0.12)",
              marginBottom: 14,
            }}
          >
            <Smartphone size={34} color="#FFD700" />
          </div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: -0.3 }}>
            WiseDrive Mechanic
          </h1>
          <p style={{ margin: "6px 0 0", fontSize: 14, opacity: 0.85 }}>
            Install the latest app for field mechanics
          </p>
        </div>

        <div style={{ padding: "26px 28px 30px" }}>
          {loading && (
            <div
              data-testid="mechanic-download-loading"
              style={{ textAlign: "center", padding: "30px 0", color: "#555" }}
            >
              <RefreshCw size={26} className="animate-spin" style={{ marginBottom: 10 }} />
              <div style={{ fontSize: 14 }}>Fetching the latest build…</div>
            </div>
          )}

          {!loading && error && (
            <div data-testid="mechanic-download-error" style={{ textAlign: "center" }}>
              <p style={{ color: "#b91c1c", fontSize: 14, margin: "0 0 16px" }}>{error}</p>
              <button
                data-testid="mechanic-download-retry"
                onClick={load}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  background: "#2E3192",
                  color: "#fff",
                  border: "none",
                  borderRadius: 12,
                  padding: "12px 20px",
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                <RefreshCw size={16} /> Retry
              </button>
            </div>
          )}

          {!loading && !error && data && (
            <>
              <div
                data-testid="mechanic-download-version"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  marginBottom: 22,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    background: "#EEF0FF",
                    color: "#2E3192",
                    fontWeight: 700,
                    fontSize: 13,
                    padding: "6px 12px",
                    borderRadius: 999,
                  }}
                >
                  Version {data.version}
                  {data.buildNumber ? ` (build ${data.buildNumber})` : ""}
                </span>
                <span
                  style={{
                    background: "#ECFDF3",
                    color: "#067647",
                    fontWeight: 700,
                    fontSize: 13,
                    padding: "6px 12px",
                    borderRadius: 999,
                  }}
                >
                  Android
                </span>
              </div>

              <a
                data-testid="mechanic-download-apk-button"
                href={downloadUrl}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  background: "#FFD700",
                  color: "#1b1d55",
                  textDecoration: "none",
                  borderRadius: 14,
                  padding: "16px 20px",
                  fontSize: 17,
                  fontWeight: 800,
                  boxShadow: "0 8px 20px rgba(255,215,0,0.35)",
                }}
              >
                <Download size={22} /> Download APK
              </a>

              <div style={{ textAlign: "center", marginTop: 26 }}>
                <div
                  data-testid="mechanic-download-qr"
                  style={{
                    display: "inline-block",
                    background: "#fff",
                    padding: 14,
                    borderRadius: 16,
                    border: "1px solid #EEF0FF",
                  }}
                >
                  <QRCode value={pageUrl} size={150} fgColor="#2E3192" />
                </div>
                <p style={{ margin: "12px 0 0", fontSize: 13, color: "#667085" }}>
                  Scan to open this page on a mechanic's phone
                </p>
              </div>

              <div
                style={{
                  marginTop: 24,
                  background: "#F9FAFB",
                  borderRadius: 12,
                  padding: "14px 16px",
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                }}
              >
                <ShieldCheck size={18} color="#2E3192" style={{ marginTop: 2, flexShrink: 0 }} />
                <p style={{ margin: 0, fontSize: 12.5, color: "#475467", lineHeight: 1.5 }}>
                  After downloading, tap the file to install. If prompted, allow
                  <b> "Install from unknown sources"</b> for your browser — this is a
                  direct company install, not from the Play Store.
                </p>
              </div>

              {fmtDate(data.createdAt) && (
                <p
                  style={{
                    margin: "18px 0 0",
                    fontSize: 11.5,
                    color: "#98A2B3",
                    textAlign: "center",
                  }}
                >
                  Latest build · {fmtDate(data.createdAt)}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
